/**
 * Roof — Phase 3's first category, reworked in the ROOF round (2026-08) around the
 * BEARING-LINE model: the `boundary` (closed Path2D) is the BEARING line — drawn at the
 * INTERIOR face of stud — and `plateHeightFt` is the BEARING elevation (level elevation +
 * plateHeightFt = where the birdsmouth seats on the plate). Slope planes spring from that
 * line at that height, and assembly thickness stacks UP from the bearing plane — changing
 * the roof build-up never moves the plate. Eaves extend `overhangFt` beyond the bearing
 * line per edge; each boundary edge carries optional per-edge attrs on the node LEAVING
 * it (PathNode.edge — slopeRise / overhangFt / gable), defaulting to the TYPE's
 * defaultSlopeRise / overhangFt (firm standards, per the types doctrine — moved from
 * instance params in the ROOF round). Hips/gables/sheds all fall out of the same
 * per-edge plane model: a shed is a first-class roof (one sloped bearing edge, the rest
 * gable rakes), never a degenerate special case.
 *
 * Surface overrides (ROOF round): instance params selecting a MATERIAL per face group —
 * TOP / BOTTOM / EDGES — '' = assembly-derived. They paint the SURFACE only; cut poché
 * stays the assembly layers'. Ordinary non-positional params, so eyedropper/inject
 * transfers them (r19).
 *
 * EAVE STYLING IS PER-INSTANCE (Matt, 2026-08-10, superseding where E1–E3 first put
 * these on the type): the TYPE stays the STRUCTURE standard (style, slope/overhang
 * defaults, rafter size + spacing); the eave expression — section crop, boxed/open
 * mode, tails, decking, fascia, gutters — is project styling and lives on the
 * INSTANCE, so the building body follows the firm standard while each roof's eaves
 * meet the project's requirements. Per-edge toggles (node.edge) override the
 * instance values exactly as they previously overrode the type's.
 */
import type { CategorySchema } from '../../registry'
import { INSULATION_PARAM_GROUP } from '../../insulationSpec'
import {
  FASCIA_SIZES, GUTTER_PROFILES, GUTTER_SIZES, OUTLOOKER_SIZES,
  FRIEZE_SIZES, RAFTER_SIZES, RAFTER_SPACINGS, SOFFIT_FRAMING_SIZES, TAIL_PROFILES, TAIL_SIZES,
} from '../../roofFraming'
import { ROOF_CORE_TYPES } from '../../roofCore'
import { TJI_JOIST_SIZES } from '../../floorCore'

const sizeChoices = (sizes: typeof RAFTER_SIZES) => sizes.map((s) => ({ value: s.key, label: s.label }))

export const roofSchema: CategorySchema = {
  id: 'roof',
  pathParamKey: 'boundary',
  label: 'Roof',
  usesAssembly: 'roof',
  typeParams: {
    groups: [
      {
        label: 'Construction',
        params: [
          {
            key: 'style', label: 'Style', kind: 'choice', default: 'hip',
            choices: [
              { value: 'hip', label: 'Hip (per-edge slopes)' },
              { value: 'flat', label: 'Flat (all edges 0 slope)' },
            ],
          },
          // min 0.25 for the same slope-0-guard reason as the instance override
          { key: 'defaultSlopeRise', label: 'Default Slope (rise/12)', kind: 'number', default: 6, min: 0.25, max: 24 },
          { key: 'overhangFt', label: 'Default Overhang', kind: 'length', default: 1.5, min: 0, max: 10 },
          {
            // ROOF CORE DEFINITION round (2026-08-18, Matt — the wall/floor pattern):
            // the declared rafter family + size DRIVE the assembly's core layer
            // (material 'Wood Rafter' / 'TJI Joist', thickness = the actual depth)
            // AND the E2 derived-framing member size — one declaration, no drift
            // between the drawn mass and the derived members. `rafterSize` is the
            // E2 param, kept: existing types' stored values carry straight over.
            key: 'coreType', label: 'Core', kind: 'choice', default: 'wood',
            choices: ROOF_CORE_TYPES.map((t) => ({ value: t.value, label: t.label })),
          },
          {
            key: 'rafterSize', label: 'Rafter Size', kind: 'choice', default: '2x10',
            conditionalOn: { param: 'coreType', equals: 'wood' },
            choices: sizeChoices(RAFTER_SIZES),
          },
          {
            key: 'tjiRafterSize', label: 'Rafter Size', kind: 'choice', default: '11-7/8',
            conditionalOn: { param: 'coreType', equals: 'tji' },
            choices: TJI_JOIST_SIZES.map((s) => ({ value: s.value, label: s.label })),
          },
          {
            // BARREL-TILE ROOFING (docs/bim-plan/12, resolved 2026-08-19): geometric
            // roofing DERIVED off the solved faces + skeleton edges when a profile
            // is declared — a firm standard on the TYPE, per the types doctrine.
            // '' = the assembly finish layer stays a flat painted surface (comp
            // shingle reads fine as texture). Visibility is the per-view Roofing
            // system check (ViewSettingsCard); this param decides WHAT derives.
            // PAINTED ROOFING (2026-09-28): retired — roofing is PAINTED per plane now
            // (a material's roofing spec). Kept readable for the one-time upgrade.
            key: 'roofingProfile', label: 'Roofing', kind: 'choice', default: '', hidden: true,
            choices: [
              { value: '', label: 'Finish material only' },
              { value: 'barrel', label: 'Two-Piece Barrel Tile' },
            ],
          },
          // Q7 blend, selectable per TYPE (Matt: "allow us to select the blend
          // colors in that layer of the assembly, too"): the derived field spreads
          // its tiles across these three shades. Default ('') = the seeded firm
          // shades (Clay Tile — Blend A/B/C, resolved via roofTileMaterials).
          { key: 'tileBlend1', label: 'Tile Blend Color 1', kind: 'material', default: '', optional: true, hidden: true, conditionalOn: { param: 'roofingProfile', equals: 'barrel' } },
          { key: 'tileBlend2', label: 'Tile Blend Color 2', kind: 'material', default: '', optional: true, hidden: true, conditionalOn: { param: 'roofingProfile', equals: 'barrel' } },
          { key: 'tileBlend3', label: 'Tile Blend Color 3', kind: 'material', default: '', optional: true, hidden: true, conditionalOn: { param: 'roofingProfile', equals: 'barrel' } },
        ],
      },
      // INSULATION SCHEDULE round (2026-10-03): the type's insulation spec — feeds the
      // derived, fit-checked insulation schedule on the General Notes sheet (insulation.ts)
      INSULATION_PARAM_GROUP,
    ],
  },
  instanceParams: {
    groups: [
      {
        // ROOF SETTINGS CLEANUP (2026-10-08): the groups are named and ordered the way the
        // palette shows them (Shape → Eave → Tails → Fascia → Gutters → Rakes → Framing →
        // Surfaces); keys and storage are unchanged
        label: 'Shape',
        params: [
          { key: 'boundary', label: 'Bearing Line', kind: 'path2d', default: null },
          // PHASE SPLIT round: opaque, never palette-rendered/transferred — same
          // treatment as floor/ceiling's own phaseSplits (types.ts#PhaseSplitEntry).
          { key: 'phaseSplits', label: 'Phase Splits', kind: 'data', default: null, hidden: true },
          // PHASE AREAS (2026-10-30): closed areas sketched in the outline editor (types.ts#PhaseAreaEntry)
          { key: 'phaseAreas', label: 'Phase Areas', kind: 'data', default: null, hidden: true },
          // PHASED SETTINGS SETS (styleSets.ts): { eaves: <setId> | 'base' } — this roof
          // keeps one Project Settings set instead of following the one standing in the view
          { key: 'styleSetPins', label: 'Kept Settings Sets', kind: 'data', default: null, hidden: true },
          // SCOPE ON THE ELEMENT (scope.ts): what the remodel does to each part of this roof
          { key: 'scope', label: 'Scope', kind: 'data', default: null, hidden: true },
          // VERTICAL CUTS (2026-10-08, Revit's Vertical Opening): closed plan loops cut
          // straight down through the finished roof AFTER it is solved — they never feed
          // the slope/skeleton logic. Everything the roof derives inside a loop (body,
          // framing, tails, fascia, gutters, soffit, downspouts, roofing) is removed and
          // the cut edges stay BARE (no eave dressing). Edited in its own sketch session
          // (Vertical Cut on the roof's Modify tab); rides every move/rotate/mirror.
          { key: 'verticalCuts', label: 'Vertical Cuts', kind: 'data', default: null, hidden: true },
          { key: 'plateHeightFt', label: 'Bearing (Plate) Height', kind: 'length', default: 9, min: 0, max: 60 },
          // CRICKET (2026-10-19, Matt): a cricket is drawn like a roof but stops dead at its
          // bearing line — no overhang and no eave or rake dressing at all. The one rule lives
          // in doc.ts#resolveElement (overhang 0, every eave/rake toggle off, per-edge eave
          // attrs ignored); every eave/rake setting below hides while it's on (CRICKET_GROUPS).
          { key: 'cricket', label: 'Cricket (no overhang, no eave or rake trim)', kind: 'bool', default: false },
          // ROOF round r2 (Matt: "can't edit the pitch") — per-roof OVERRIDES of the
          // type standards, per the standing clarification's corollary: every derived
          // behavior has a designer override; derived is the DEFAULT, never a CAGE.
          // null/empty = the type's value. NOTE `overhangFt` deliberately SHADOWS the
          // type key in resolved params — read both through solve.ts#roofDefaultsFor,
          // never `el.params.overhangFt` alone.
          // min 0.25, not 0 (2026-08-19 slope-0 guard): a stored slope of 0 on a
          // pitched roof disqualifies the WHOLE roof from the skeleton solver and
          // silently drops it to the adjacency-free envelope (gables stop
          // responding, low planes sweep the footprint). A true flat roof is the
          // 'flat' STYLE, which never reads slopeRise.
          // REVERSED PITCH (2026-10-19): negative = the roof falls inward from its bearing
          // line (butterfly / inverted hip); every entry point checks roofSlopeProblem first
          { key: 'slopeRise', label: 'Slope (rise/12)', kind: 'number', default: null, optional: true, min: -24, max: 24 },
          { key: 'overhangFt', label: 'Overhang', kind: 'length', default: null, optional: true, min: 0, max: 10, conditionalOn: { param: 'cricket', equals: false } },
          {
            // ROOF ROUND E5 r2 (roof-to-roof composition — JOIN, superseding r1's
            // footprint trimming, which broke the tails' even rhythm and hit both
            // sides of the roof): the ONE roof this roof JOINS. On eave edges that
            // are IN ALIGNMENT with the target's (collinear bearing lines, same
            // overhang/slope/plate — the eave lines coincide), tail stations lay
            // over the MERGED span in one rhythm, each roof emitting its own
            // stretch (the target owns overlaps), and fascia/gutter runs butt the
            // target's instead of doubling under them; the joiner also ADOPTS the
            // target's eave member SETTINGS (sizes/spacing/styles/materials —
            // the on/off toggles stay per-roof, they drive the mass's decking
            // read). Managed by the pet palette's Join eaves… pick flow, never a
            // form field; positional (a relationship to the specific partner —
            // derivation never auto-couples). Non-aligned edges are untouched;
            // body geometry is untouched (the full body trim stays the recorded
            // follow-on).
            key: 'joinRoofId', label: 'Joined Roof', kind: 'data',
            default: null, positional: true, hidden: true,
          },
          {
            // OVERFRAME (2026-10-08): the roofs this one is overframed (California framed) onto —
            // wherever one of them rides at or above this roof inside its bearing footprint, this
            // roof isn't built; the valley lines derive live (bim-geometry roof/overframe.ts).
            // Never adopts their eave settings; any number of roofs.
            key: 'overframeRoofIds', label: 'Overframed Onto', kind: 'data',
            default: null, positional: true, hidden: true,
          },
        ],
      },
      {
        // EAVE STYLING (instance — see the header note). `eaveMode`: BOXED keeps the
        // eave zone a solid mass (soffit read; E1's crop shapes its depth); OPEN
        // thins the eave mass to the decking + finishes and EXPRESSES the eave-zone
        // structure as real members in 3D — exposed field-rafter ends (and hip/
        // valley ends) render always, no framing-study toggle needed; E1's crop then
        // shapes those exposed ends. Body framing stays inside the mass either way
        // (the Show Framing study toggle below). E1 crop ('' = full depth) applies
        // when boxed / to the exposed members when open; per-edge override rides
        // PathNode.edge.eaveStructureDepthIn. Flat roofs stay boxed (open-joist
        // expression deferred with flat framing).
        label: 'Eave',
        params: [
          {
            key: 'eaveMode', label: 'Eave Mode', kind: 'choice', default: 'boxed',
            choices: [
              { value: 'boxed', label: 'Boxed (soffit)' },
              { value: 'open', label: 'Open (exposed structure)' },
            ],
          },
          {
            key: 'eaveStructureDepthIn', label: 'Eave Structure Depth', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'Full depth (no crop)' },
              { value: '3.5', label: 'Crop to 2×4 — 3½″' },
              { value: '5.5', label: 'Crop to 2×6 — 5½″' },
              { value: '7.25', label: 'Crop to 2×8 — 7¼″' },
              { value: '9.25', label: 'Crop to 2×10 — 9¼″' },
              { value: '11.25', label: 'Crop to 2×12 — 11¼″' },
            ],
          },
          { key: 'deckingThicknessIn', label: 'Eave Decking (in)', kind: 'number', default: 0.75, min: 0.25, max: 3 },
          // blank = the roof framing material
          { key: 'deckingMaterial', label: 'Open Eave Decking Material', kind: 'material', default: '' },
          { key: 'soffitMaterial', label: 'Soffit Material (boxed / flat)', kind: 'material', default: '' },
          {
            // FLAT SOFFIT + CORBELS round (2026-10-07, Matt): the boxed eave's UNDERSIDE.
            // SLOPED = the soffit follows the rafters (every roof before this round).
            // FLAT = the soffit returns LEVEL from the fascia back to the wall at the
            // underside of the eave structure (the E1 crop above sets that depth, so it
            // is the fascia depth); a tails edge then expresses its tails as CORBELS —
            // level members of the tail stock under the flat soffit, from the bearing
            // wall's outside face to the fascia, never reaching back into the building.
            // At a gable corner the flat soffit RETURNS along the rake for
            // soffitReturnFt (the cornice return that cleans up the eave→rake
            // transition) before the rake soffit resumes sloped. Per-edge override:
            // node.edge.flatSoffit (adds, like every bool edge override). Geometry:
            // bim-geometry categories/roof/soffit.ts.
            key: 'soffitStyle', label: 'Soffit', kind: 'choice', default: 'sloped',
            choices: [
              { value: 'sloped', label: 'Sloped (follows the rafters)' },
              { value: 'flat', label: 'Flat (level to the wall; tails become corbels)' },
            ],
          },
          { key: 'soffitReturnFt', label: 'Soffit Return at Rakes', kind: 'length', default: 1.5, min: 0, max: 8 },
          {
            // FLAT SOFFIT FRAMING (Matt, 2026-10-07): the ledger on the outer face of the
            // wall sheathing + the lookouts from it to the back of the fascia, laid at the
            // rafter spacing, on the soffit panel — study members, shown with the roof
            // framing. The soffit's own elevation is the FASCIA's: ½″ above the fascia
            // board's bottom (soffit.ts#flatSoffitZ), so it never pops below the fascia.
            key: 'soffitFramingSize', label: 'Soffit Framing', kind: 'choice', default: '2x4',
            choices: sizeChoices(SOFFIT_FRAMING_SIZES),
          },
        ],
      },
      {
        // E2 tails (instance eave styling): the SHOW members — the framing of the
        // EAVES, independent of (and not necessarily aligned with) the roof's own
        // field rafters. `tailsDefault` puts them on every eave edge of THIS roof;
        // node.edge.tails overrides per edge (a per-edge value only ADDS — the bool
        // edge form's recorded limitation). Reach-in = 2× each edge's eave depth
        // (derived, never typed); the decking above them reads from the Eaves
        // group. SPACING IS A MAX (Matt, 2026-08-11): tails distribute EQUALLY
        // along their eave edge, a member at each end, actual spacing ≤ the max.
        label: 'Rafter Tails',
        params: [
          { key: 'tailsDefault', label: 'Tails on all eaves', kind: 'bool', default: false },
          { key: 'tailSize', label: 'Tail Size', kind: 'choice', default: '4x8', choices: sizeChoices(TAIL_SIZES) },
          { key: 'tailSpacingIn', label: 'Tail Max Spacing (in)', kind: 'number', default: 24, min: 12, max: 72 },
          {
            // E4 r3: the 15-profile chart family + Straight Cut (the no-style
            // default); legacy taper/ogee/plumbSeat values alias in tailProfile()
            key: 'tailStyle', label: 'Tail Style', kind: 'choice', default: 'straight',
            choices: TAIL_PROFILES.map((p) => ({ value: p.key, label: p.label })),
          },
          // COLORS (2026-10-05): the Paint Brush's color on every tail of this roof (3D only)
          // FRIEZE BOARD (2026-10-07, Matt: "a toggle to turn on/off the frieze board between
          // tails/corbels and to set the frieze board's size"): the trim board on the wall's
          // outside face along a tails eave — NOTCHED around the tails, its top cut to the
          // decking's slope; under a flat soffit it runs continuous behind the corbels (the
          // corbels mount over it), top against the soffit. Takes the fascia's finish and
          // the Trim color (the Paint Brush paints it with the fascia). Per edge:
          // node.edge.frieze (adds, like every bool edge override); needs tails on the edge.
          { key: 'friezeDefault', label: 'Frieze board between tails / corbels', kind: 'bool', default: false },
          { key: 'friezeSize', label: 'Frieze Board Size', kind: 'choice', default: '1x8', choices: sizeChoices(FRIEZE_SIZES) },
          { key: 'tailColor', label: 'Tail Color', kind: 'data', default: null, hidden: true },
          // FASCIA + SOFFIT COLOR (2026-10-07): the Paint Brush's colors (colors.ts refs)
          { key: 'fasciaColor', label: 'Fascia Color', kind: 'data', default: null, hidden: true },
          { key: 'soffitColor', label: 'Soffit Color', kind: 'data', default: null, hidden: true },
          // PAINT BRUSH (2026-10-07): the brushed color (a colors.ts ref) — blank = its finish / role
          { key: 'gutterColor', label: 'Gutter Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        // E3 fascia + gutters (instance eave styling), derived along the skeleton's
        // classified edges. Per-edge toggles ride node.edge.fascia/gutter over the
        // two all-edges defaults here (a per-edge value only ADDS — forcing an edge
        // OFF against a default-on roof is a recorded v1 limitation of the bool edge
        // form). Gutters apply on EAVE edges only; fascia follows eaves (level) and
        // rakes (sloped) with mitered corners. GUTTER PROFILES: half-round / box /
        // tapered ONLY — K-style is deliberately absent (enforcement by omission).
        label: 'Fascia',
        params: [
          { key: 'fasciaDefault', label: 'Fascia on all edges', kind: 'bool', default: false },
          { key: 'fasciaSize', label: 'Fascia Size', kind: 'choice', default: '2x8', choices: sizeChoices(FASCIA_SIZES) },
          { key: 'fasciaMaterial', label: 'Fascia Material', kind: 'material', default: '' },
        ],
      },
      {
        label: 'Gutters & Downspouts',
        params: [
          { key: 'gutterDefault', label: 'Gutters on all eaves', kind: 'bool', default: false },
          {
            key: 'gutterProfile', label: 'Gutter Profile', kind: 'choice', default: 'halfRound',
            choices: GUTTER_PROFILES.map((p) => ({ value: p.key, label: p.label })),
          },
          {
            key: 'gutterSize', label: 'Gutter Size', kind: 'choice', default: '5',
            choices: GUTTER_SIZES.map((s) => ({ value: s.key, label: s.label })),
          },
          { key: 'gutterMaterial', label: 'Gutter Material', kind: 'material', default: '' },
          // DOWNSPOUTS (2026-10-04, Matt): the roof's DEFAULTS for every downspout off its
          // gutters — automatic or placed; any single downspout can override them
          {
            key: 'downspoutShape', label: 'Downspout Shape', kind: 'choice', default: 'auto',
            choices: [
              { value: 'auto', label: 'Match gutter (round for half-round)' },
              { value: 'round', label: 'Round' },
              { value: 'rect', label: 'Rectangular' },
            ],
          },
          {
            key: 'downspoutBend', label: 'Downspout Bends', kind: 'choice', default: 'radius',
            choices: [{ value: 'radius', label: 'Welded, radiused' }, { value: 'miter', label: 'Welded, mitered' }],
          },
          {
            key: 'downspoutEnd', label: 'Downspout Ends', kind: 'choice', default: 'grade',
            choices: [{ value: 'grade', label: 'Spill to grade (kick-out + splash block)' }, { value: 'subdrain', label: 'Into sub-drain at grade' }],
          },
        ],
      },
      {
        // E4 rake outlookers + barge rafters (instance eave styling): the tails
        // pattern applied to RAKES. `bargeDefault` puts the treatment on every rake
        // of THIS roof; node.edge.barge overrides per (gable) edge — a per-edge
        // value only ADDS, the bool edge form's recorded limitation. E4 r2 (Matt,
        // with reference photo): outlookers are DECORATIVE BRACKETS — set BELOW the
        // barge rafter, projecting from the rake wall out to the barge's outer
        // face, laid along the rake to a MAX spacing (plan-measured, equally
        // distributed — tailStations, clear of the barge's shaped tip), outer ends
        // shaped by the end style below. ONE barge rafter per rake seats on them,
        // its OUTER face aligned with the roof edge (the sheathing line) — size
        // defaults to the roof's tail size, lower end cut with the roof's tail
        // style (the coherent-look requirement: same profile, same intrinsic run —
        // see the PROFILE RUN rule in bim-geometry framing.ts), upper ends meeting
        // plumb at the ridge against the partner barge. A barge rake SUPPRESSES E3
        // fascia on that edge (the barge IS the rake trim — alternatives, never
        // stacked); gutters are eave-only already, no interaction.
        label: 'Rakes: Outlookers & Barge',
        params: [
          { key: 'bargeDefault', label: 'Outlookers + barge on all rakes', kind: 'bool', default: false },
          { key: 'outlookerSize', label: 'Outlooker Size', kind: 'choice', default: '4x6', choices: sizeChoices(OUTLOOKER_SIZES) },
          // max raised 72 → 192 (16′) in E4 r4 — widely spaced heavy brackets
          // are a legitimate composition (Matt)
          {
            // OUTLOOKER COUNT (2026-10-09): place by max spacing, or exactly N per rake, equally spaced
            key: 'outlookerLayout', label: 'Outlooker Layout', kind: 'choice', default: 'spacing',
            choices: [{ value: 'spacing', label: 'By max spacing' }, { value: 'count', label: 'Exact number, equally spaced' }],
          },
          { key: 'outlookerSpacingIn', label: 'Outlooker Max Spacing (in)', kind: 'number', default: 24, min: 12, max: 192 },
          { key: 'outlookerCount', label: 'Outlookers per Rake', kind: 'number', default: 3, min: 1, max: 24 },
          {
            // E4 r3: the SAME 15-profile chart family as the tails (one profile
            // language across tails, bracket ends, and the barge's lower cut)
            key: 'outlookerStyle', label: 'Outlooker End Style', kind: 'choice', default: 'straight',
            choices: TAIL_PROFILES.map((p) => ({ value: p.key, label: p.label })),
          },
          {
            key: 'bargeSize', label: 'Barge Size', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Match tail size' }, ...sizeChoices(TAIL_SIZES)],
          },
          // COLORS (2026-10-05): the Paint Brush's color per member kind, this roof (3D only)
          { key: 'outlookerColor', label: 'Outlooker Color', kind: 'data', default: null, hidden: true },
          { key: 'bargeColor', label: 'Barge Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        // E2: the framing-STUDY toggle (body members inside the mass) — a design
        // study on one roof, not a firm standard. Open eaves express the EAVE-zone
        // members regardless of this toggle; tails likewise (show architecture).
        // ROOF CORE DEFINITION round: rafter SPACING moved here from the type (the
        // wall studSpacingIn precedent — every roof of a type doesn't necessarily
        // want the same module).
        label: 'Framing',
        params: [
          {
            // ASSEMBLY INSULATION (2026-10-14, Matt: "floor, roof, and ceilings to have
            // insulation as instance parameters like walls do"): batt-hatches the CORE band
            // in section (section.ts's assembly band pass), framing members masking it.
            // No 3D effect — the wall `insulation` toggle's exact contract.
            key: 'insulation', label: 'Insulation', kind: 'bool', default: false,
            conditionalOn: { param: 'coreType', equals: ['wood', 'tji', 'metal'] },
          },
          { key: 'showFraming', label: 'Show Framing', kind: 'bool', default: false },
          {
            key: 'rafterSpacingIn', label: 'Rafter Spacing', kind: 'choice', default: '24',
            choices: RAFTER_SPACINGS.map((s) => ({ value: String(s), label: `${s}″ o.c.` })),
          },
        ],
      },
      {
        // SURFACES RETIRED (2026-10-08, Matt: "a carry over from ArchiCAD inspiration and no longer
        // relevant since we use the material applicator and paint brush tools"): no longer shown
        // anywhere. The keys stay (hidden) so a model that set them still draws the same — the
        // geometry and the roof-plan hatch read them; nothing in the app writes them anymore.
        label: 'Surfaces',
        params: [
          { key: 'topMaterial', label: 'Top Surface', kind: 'material', default: '', hidden: true },
          { key: 'bottomMaterial', label: 'Bottom Surface', kind: 'material', default: '', hidden: true },
          { key: 'edgeMaterial', label: 'Edge Surfaces', kind: 'material', default: '', hidden: true },
        ],
      },
    ],
  },
}

/** CRICKET: the instance groups that are eave / rake dressing — every setting in them hides
 *  (and resolves to its default) while the roof is a cricket. Kept as a post-pass so each
 *  group above keeps its own conditions; a cricket condition is ANDed onto them. */
const CRICKET_GROUPS = new Set(['Eave', 'Rafter Tails', 'Fascia', 'Gutters & Downspouts', 'Rakes: Outlookers & Barge'])
const NOT_CRICKET = { param: 'cricket', equals: false }
for (const g of roofSchema.instanceParams.groups) {
  if (!CRICKET_GROUPS.has(g.label)) continue
  for (const d of g.params) {
    const prev = d.conditionalOn
    d.conditionalOn = prev == null ? NOT_CRICKET : [...(Array.isArray(prev) ? prev : [prev]), NOT_CRICKET]
  }
}
